import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { meetingsApi } from '../features/meetings/api.ts';
import {
  notesApi, pageIdOf, pageLabel, noteQueryKey, pagesQueryKey, GENERAL_PAGE, type PageId,
} from '../features/notes/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { NoteEditor } from '../features/notes/NoteEditor.tsx';
import { ScriptView } from '../features/notes/ScriptView.tsx';
import { noteBodyToHtml } from '../lib/noteHtml.ts';
import { Icon } from '../components/Icon.tsx';

const SAVE_DEBOUNCE_MS = 1000;
const SAVE_RETRY_MS = 3000;

interface SaveArgs { meetingId: string; pageId: PageId; body: string }

// ONE page of a person's private notebook for a meeting: either the
// meeting-wide general pad (`pageId === 'general'`, where the chair's script
// goes) or one agenda item's page. Strictly private — never shared with anyone
// else, not even other writers or the president.
//
// Everything meeting-scoped in here is keyed on `(meetingId, pageId)` rather
// than on the meeting alone. That pairing is load-bearing: the router reuses
// this component across a $pageId change, so a value keyed on the meeting would
// leave page A's text in the editor while the saves targeted page B.
//
// `text` holds the note as HTML. Bodies saved before the notepad became a rich
// text editor are plain text and are converted on load by noteBodyToHtml; the
// column was never migrated, so that conversion is the only thing standing
// between a legacy note and an editor that would otherwise show its markup.
export function MeetingNotePage(): JSX.Element {
  const { meetingId, pageId } = useParams({ from: '/meetings/$meetingId/notes/$pageId' });
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? null;
  const isGeneral = pageId === GENERAL_PAGE;

  const meeting = useQuery({ queryKey: ['meeting', meetingId], queryFn: () => meetingsApi.get(meetingId) });
  // Deduped against the layout's identical query — read here only for this
  // page's heading, never fetched twice.
  const pages = useQuery({
    queryKey: pagesQueryKey(userId, meetingId),
    queryFn: () => notesApi.fetchPages(meetingId),
    enabled: userId !== null,
  });
  // The note is PRIVATE, so its cache entry is scoped to the VIEWER as well as
  // the meeting and page. `ssoBootstrap(true)` (from the 401 interceptor) can
  // swap the authenticated user in place with no reload and no cache clear; on
  // a shared council-office machine an unscoped key would hand user A's note to
  // user B, whose next keystroke would save it into B's note.
  const note = useQuery({
    queryKey: noteQueryKey(userId, meetingId, pageId),
    queryFn: () => notesApi.fetchNote(meetingId, pageId),
    enabled: userId !== null,
  });

  const [text, setText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [failed, setFailed] = useState(false);
  // Full-screen read-only lectern view. Purely presentational: it renders the
  // CURRENT document (including unsaved edits) and can neither edit nor save.
  const [presenting, setPresenting] = useState(false);
  // Which page the editor has been seeded FOR. State, not a ref, because the
  // render has to flip out of the loading branch when it changes — and a note
  // whose body is '' would not re-render off `setText` alone.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const seedToken = `${meetingId}:${pageId}`;
  const seeded = seededFor === seedToken;
  // Bumped ONLY when the document has to be replaced wholesale (regenerate).
  // It is part of the editor's `key`, so a replacement is a fresh editor
  // mounted on the new content rather than content pushed into a live one —
  // pushing would fire TipTap's own update event, and an update is a save.
  const [editorSeed, setEditorSeed] = useState(0);

  // Refs, never state, for anything a cleanup or a timer reads: a cleanup
  // closure captures the state value from the render it was created in.
  const pending = useRef<SaveArgs | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retried = useRef(false);
  const mutateRef = useRef<((v: SaveArgs) => void) | null>(null);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  // Whether this page counted as "written on" at the last save. The page list
  // shows a dot per written page, so it only needs refetching when that flips —
  // not after every debounced save.
  const hadNote = useRef<boolean | null>(null);

  const save = useMutation({
    mutationFn: (v: SaveArgs) => notesApi.saveNote(v.meetingId, v.pageId, v.body),
    onSuccess: (saved, v) => {
      qc.setQueryData(noteQueryKey(userIdRef.current, v.meetingId, v.pageId), saved);
      const nowHasNote = saved.body !== '';
      if (hadNote.current !== nowHasNote) {
        hadNote.current = nowHasNote;
        void qc.invalidateQueries({ queryKey: pagesQueryKey(userIdRef.current, v.meetingId) });
      }
      // Only settle if nothing newer was typed while this save was in flight.
      if (pending.current && pending.current.meetingId === v.meetingId
        && pending.current.pageId === v.pageId && pending.current.body === v.body) {
        pending.current = null;
        setDirty(false);
      } else if (pending.current === null) {
        setDirty(false);
      }
      setFailed(false);
      retried.current = false;
    },
    onError: (_err, v) => {
      // Never fail silently: a 500/429/network error must not look like idle.
      // The text stays dirty (so beforeunload still warns) and we try once more.
      setFailed(true);
      setDirty(true);
      if (pending.current === null) pending.current = v;
      if (retried.current) return;
      retried.current = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        if (pending.current) mutateRef.current?.(pending.current);
      }, SAVE_RETRY_MS);
    },
  });
  mutateRef.current = save.mutate;

  function saveNow(args: SaveArgs): void {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    retried.current = false;
    mutateRef.current?.(args);
  }

  function onChange(value: string): void {
    // Never write before a successful load: an unseeded editor would PUT its
    // first keystroke over whatever is really saved on the server.
    if (!seeded) return;
    setText(value);
    setDirty(true);
    setFailed(false);
    retried.current = false;
    pending.current = { meetingId, pageId, body: value };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      if (pending.current) mutateRef.current?.(pending.current);
    }, SAVE_DEBOUNCE_MS);
  }

  // Re-initialise everything page-scoped when the route params change: the
  // router reuses this component across a $meetingId OR $pageId change, so
  // without this, page A's text stays in the editor while saves target page B.
  // Declared BEFORE the seeding effect so that on a param change this runs
  // first and cannot wipe a freshly seeded value.
  useEffect(() => {
    setSeededFor(null);
    setText('');
    setDirty(false);
    setFailed(false);
    retried.current = false;
    hadNote.current = null;
    return () => {
      // Flush — never drop — a debounced save when leaving this page.
      // `beforeunload` covers only tab close/refresh; SPA navigation (the Back
      // link, or switching to another page in the list) fires nothing, so a
      // sentence typed within the debounce window would simply vanish. Fired
      // against the meeting AND page the text was typed for, taken from a ref
      // so no stale closure can retarget it.
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
      const p = pending.current;
      pending.current = null;
      if (!p) return;
      const uid = userIdRef.current;
      void notesApi.saveNote(p.meetingId, p.pageId, p.body)
        .then((saved) => {
          qc.setQueryData(noteQueryKey(uid, p.meetingId, p.pageId), saved);
          // The page list may now need a dot it did not have when we left.
          void qc.invalidateQueries({ queryKey: pagesQueryKey(uid, p.meetingId) });
        })
        .catch(() => { /* the page is already gone — nothing left to surface */ });
    };
  }, [meetingId, pageId, qc]);

  // Seed the editor from the server EXACTLY ONCE per page. Re-seeding on every
  // query update (e.g. after our own save invalidates/refetches) would
  // overwrite whatever the user is currently typing — a mistake already made
  // once in this codebase.
  //
  // The seed must come from a FRESH read. gcTime is 5 minutes, so a remount can
  // be handed stale cached data; seeding from it would show the chair a blank
  // notepad when their script was already rendered into it, and the first
  // keystroke would then overwrite that script.
  useEffect(() => {
    if (seededFor === seedToken) return;
    if (!note.isSuccess || !note.isFetchedAfterMount || note.data === undefined) return;
    setText(noteBodyToHtml(note.data.body));
    setDirty(false);
    pending.current = null;
    hadNote.current = note.data.body !== '';
    setSeededFor(seedToken);
  }, [seedToken, seededFor, note.isSuccess, note.isFetchedAfterMount, note.data]);

  // Warn on tab-close/refresh while a debounced save hasn't landed yet.
  useEffect(() => {
    function handler(e: BeforeUnloadEvent): void {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Only the acting chair may regenerate, and only onto the GENERAL pad: the
  // script is one continuous document for the whole meeting, so it has no
  // meaning on a single agenda item's page.
  const isChair = !!meeting.data && !!user && meeting.data.actingChairId === user.id;

  function regenerate(): void {
    if (!seeded || !isGeneral) return;
    if (!window.confirm(dv.notes.regenerateConfirm)) return;
    setFailed(false);
    // Read-only fetch of the rendered script. Persisted IMMEDIATELY rather than
    // through the 1s debounce, so navigating away right after confirming can
    // never lose the regenerated script.
    void notesApi.fetchScript(meetingId)
      .then((script) => {
        // The server renders the script as HTML; noteBodyToHtml is a no-op on
        // it and a safety net if it ever arrives as plain text again.
        const html = noteBodyToHtml(script);
        setText(html);
        setEditorSeed((n) => n + 1); // remount the editor onto the new document
        setDirty(true);
        pending.current = { meetingId, pageId, body: html };
        saveNow({ meetingId, pageId, body: html });
      })
      .catch(() => { setFailed(true); });
  }

  if (meeting.isError || note.isError) {
    return (
      <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <p className="text-sm text-muted-foreground" role="alert">{dv.common.error}</p>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => { void meeting.refetch(); void note.refetch(); }}
        >
          {dv.common.retry}
        </button>
      </section>
    );
  }

  // The editor is rendered ONLY once seeded — never blank-and-editable.
  if (!meeting.data || !user || !seeded) {
    return <p className="p-4 text-sm text-muted-foreground">{dv.common.loading}</p>;
  }

  const current = pages.data?.find((p) => pageIdOf(p) === pageId);

  return (
    <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
      {presenting && (
        <ScriptView
          html={text}
          pages={pages.data ?? []}
          activePageId={pageId}
          meetingId={meetingId}
          onClose={() => setPresenting(false)}
        />
      )}

      {current && (
        <div className="space-y-0.5">
          <h2 className="text-lg font-semibold tracking-tight">{pageLabel(current)}</h2>
          {!current.onAgenda && (
            <p className="text-xs text-muted-foreground">{dv.notes.offAgenda}</p>
          )}
        </div>
      )}

      {/* Mounted only once `seeded` is true (see the loading branch above), so
          the editor can never be edited — or fire a save — before the note has
          genuinely loaded. */}
      <NoteEditor
        key={`${meetingId}:${pageId}:${editorSeed}`}
        initialHtml={text}
        onChange={onChange}
        // Same identity as the remount key on purpose: scroll is remembered per
        // page, and a regenerate (which bumps editorSeed) replaces the whole
        // document, so restoring the old offset into it would land anywhere.
        scrollKey={`${meetingId}:${pageId}:${editorSeed}`}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setPresenting(true)} className="btn-secondary btn-sm">
          <Icon name="fullscreen" size={16} /> {dv.notes.present}
        </button>
        {isChair && isGeneral && (
          <button type="button" onClick={regenerate} className="btn-secondary btn-sm">
            {dv.notes.regenerate}
          </button>
        )}
        {failed && !save.isPending && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => { if (pending.current) saveNow(pending.current); }}
          >
            {dv.common.retry}
          </button>
        )}
        <span className="self-center text-xs text-muted-foreground" aria-live="polite">
          {save.isPending
            ? dv.yaumiyya.saving
            : failed
              ? dv.common.error
              : save.isSuccess && !dirty
                ? dv.yaumiyya.saved
                : ''}
        </span>
      </div>
    </section>
  );
}
